/**
 * Zitadel OIDC redirect-back handler for HROS.
 *
 * Mounted at `/auth/callback`. Zitadel redirects here with `?code=…&state=…`
 * after the user signs in (or registers, or resets password) on the hosted
 * UI. Exchanges the code for tokens, fetches userinfo, persists into
 * localStorage (compatible with the @yesbos/api-client auth keys), then
 * navigates to the app root.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { Loader2 } from 'lucide-react';
import { handleZitadelCallback } from '@/lib/zitadel-oidc';

export function AuthCallbackPage() {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    handleZitadelCallback(params)
      .then(() => {
        // Strip the code/state from history and go home.
        window.history.replaceState({}, '', '/');
        navigate('/', { replace: true });
        // Force Refine to re-check auth state with the new token.
        window.location.reload();
      })
      .catch((err) => {
        setError((err as Error).message);
      });
  }, [navigate]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-[var(--color-muted)] p-4">
      <div className="max-w-md text-center space-y-3">
        {!error ? (
          <>
            <Loader2 className="w-6 h-6 animate-spin mx-auto text-[var(--color-primary)]" />
            <p className="text-sm text-[var(--color-muted-foreground)]">Signing you in…</p>
          </>
        ) : (
          <>
            <div className="rounded-md bg-[var(--color-destructive)]/10 p-3 text-sm text-[var(--color-destructive)]">
              {error}
            </div>
            <button
              onClick={() => navigate('/login')}
              className="px-3 py-2 text-sm rounded bg-[var(--color-primary)] text-[var(--color-primary-foreground)]"
            >
              Try again
            </button>
          </>
        )}
      </div>
    </div>
  );
}
